// Authentication for the console.
//  - 'dev'   : identity picked in the UI, stored in localStorage, sent as x-dev-user / x-dev-roles
//              (and dev_user / dev_roles on the EventSource URL). Local devstack only.
//  - 'entra' : MSAL PublicClientApplication, loginRedirect, acquireTokenSilent → Authorization: Bearer
//              (and access_token on the EventSource URL).

import type { FeaturesConfig, Role, TelephonyProvider } from './api.ts';
import { ALL_ROLES, TELEPHONY_PROVIDERS } from './api.ts';

export interface EntraConfig {
  clientId: string;
  tenantId: string;
  apiScope: string;
}

export interface AppConfig {
  authMode: 'dev' | 'entra';
  entra?: EntraConfig;
  alarmDomain: string;
  version: string;
  /** Phase 1: active events one operator may hold (default 1). */
  maxActiveEvents: number;
  /** Phase 1: features whose controls are hidden while off. */
  features: FeaturesConfig;
  /** How the console places calls (api-phase1-telephony-audit.md). */
  telephony: TelephonyConfig;
  /** Phase 2: push routing offers events; pull lets operators claim from the queue. */
  routing: RoutingConfig;
}

export interface RoutingConfig {
  mode: 'push' | 'pull';
  /** In push mode, operators may still claim from the queue. */
  allowPull: boolean;
}

function readRouting(v: unknown): RoutingConfig {
  const r = isRecord(v) ? v : {};
  // Servers before Phase 2 have no routing block: that is pull.
  const mode = r.mode === 'push' ? 'push' : 'pull';
  return { mode, allowPull: mode === 'pull' ? true : r.allowPull === true };
}

export interface RingCentralConfig {
  /** RingCentral platform server. */
  server: string;
}

export interface TelephonyConfig {
  provider: TelephonyProvider;
  /** Set when provider is `ringcentral`. */
  ringcentral: RingCentralConfig | null;
}

function readTelephony(v: unknown): TelephonyConfig {
  const t = isRecord(v) ? v : {};
  // Servers without telephony are manual dial (api-phase1.md).
  const provider = (TELEPHONY_PROVIDERS as readonly unknown[]).includes(t.provider) ? (t.provider as TelephonyProvider) : 'manual';
  const rc = isRecord(t.ringcentral) ? t.ringcentral : null;
  const ringcentral = rc ? { server: typeof rc.server === 'string' && rc.server ? rc.server : 'https://platform.ringcentral.com' } : null;
  return { provider, ringcentral };
}

function readFeatures(v: unknown): FeaturesConfig {
  const f = isRecord(v) ? v : {};
  const on = (k: string) => f[k] === true;
  return { agent: on('agent'), park: on('park'), transfer: on('transfer'), deterrence: on('deterrence'), multiEvent: on('multiEvent'), softphone: on('comms.softphone'), tempDataWipe: on('tempDataWipe') };
}

export interface AuthProvider {
  readonly mode: 'dev' | 'entra';
  /** Headers for fetch() calls to /api/*. */
  headers(): Promise<Record<string, string>>;
  /** Query params for the EventSource URL (it cannot send headers). */
  streamParams(): Promise<Record<string, string>>;
  signOut(): Promise<void>;
}

function isRecord(v: unknown): v is Record<string, unknown> {
  return typeof v === 'object' && v !== null && !Array.isArray(v);
}

export async function loadConfig(): Promise<AppConfig> {
  let res: Response;
  try {
    res = await fetch('/config.json', { headers: { accept: 'application/json' }, cache: 'no-store' });
  } catch (e) {
    throw new Error(`Cannot reach the console backend for /config.json (${e instanceof Error ? e.message : String(e)})`);
  }
  if (!res.ok) throw new Error(`/config.json returned HTTP ${res.status}`);
  const raw: unknown = await res.json();
  if (!isRecord(raw) || (raw.authMode !== 'dev' && raw.authMode !== 'entra')) {
    throw new Error('/config.json is missing a valid authMode');
  }
  const cfg: AppConfig = {
    authMode: raw.authMode,
    alarmDomain: typeof raw.alarmDomain === 'string' ? raw.alarmDomain : '',
    version: typeof raw.version === 'string' ? raw.version : '',
    maxActiveEvents:
      typeof raw.maxActiveEvents === 'number' && Number.isFinite(raw.maxActiveEvents) && raw.maxActiveEvents >= 1
        ? Math.floor(raw.maxActiveEvents)
        : 1,
    features: readFeatures(raw.features),
    telephony: readTelephony(raw.telephony),
    routing: readRouting(raw.routing),
  };
  if (isRecord(raw.entra)) {
    const e = raw.entra;
    if (typeof e.clientId === 'string' && typeof e.tenantId === 'string' && typeof e.apiScope === 'string') {
      cfg.entra = { clientId: e.clientId, tenantId: e.tenantId, apiScope: e.apiScope };
    }
  }
  if (cfg.authMode === 'entra' && !cfg.entra) throw new Error('/config.json has authMode "entra" but no entra settings');
  return cfg;
}

/* ------------------------------------------------------------------ */
/* Dev mode                                                             */
/* ------------------------------------------------------------------ */

export interface DevIdentity {
  upn: string;
  roles: Role[];
}

const DEV_KEY = 'eyeqsoc.devIdentity';
export const DEFAULT_DEV_IDENTITY: DevIdentity = {
  upn: 'dev.admin@eyeq.local',
  roles: ['admin', 'supervisor', 'operator'],
};

export function getDevIdentity(): DevIdentity {
  try {
    const raw = localStorage.getItem(DEV_KEY);
    if (!raw) return DEFAULT_DEV_IDENTITY;
    const v: unknown = JSON.parse(raw);
    if (isRecord(v) && typeof v.upn === 'string' && Array.isArray(v.roles)) {
      const roles = v.roles.filter((r): r is Role => typeof r === 'string' && (ALL_ROLES as readonly string[]).includes(r));
      return { upn: v.upn.trim() || DEFAULT_DEV_IDENTITY.upn, roles };
    }
  } catch {
    /* storage blocked or bad JSON: fall back to default */
  }
  return DEFAULT_DEV_IDENTITY;
}

export function setDevIdentity(id: DevIdentity | null): void {
  try {
    if (id) localStorage.setItem(DEV_KEY, JSON.stringify(id));
    else localStorage.removeItem(DEV_KEY);
  } catch {
    /* ignore */
  }
}

function devProvider(): AuthProvider {
  return {
    mode: 'dev',
    async headers() {
      const id = getDevIdentity();
      return { 'x-dev-user': id.upn, 'x-dev-roles': id.roles.join(',') };
    },
    async streamParams() {
      const id = getDevIdentity();
      return { dev_user: id.upn, dev_roles: id.roles.join(',') };
    },
    async signOut() {
      setDevIdentity(null);
    },
  };
}

/* ------------------------------------------------------------------ */
/* Entra mode (MSAL)                                                    */
/* ------------------------------------------------------------------ */

const RETURN_HASH_KEY = 'eyeqsoc.returnHash';
const RENEW_REDIRECT_KEY = 'eyeqsoc.renewRedirectAt';
/** MSAL errors where the hidden-iframe renewal could not finish (third-party cookies blocked, slow network, ...). */
const SILENT_RENEWAL_FAILED = new Set(['timed_out', 'monitor_window_timeout', 'block_iframe_reload', 'iframe_closed_prematurely']);

/** Never resolves: used while the browser navigates away to Entra. */
function navigatingAway<T>(): Promise<T> {
  return new Promise<T>(() => undefined);
}

async function entraProvider(cfg: EntraConfig): Promise<AuthProvider> {
  const msal = await import('@azure/msal-browser');
  const pca = await msal.createStandardPublicClientApplication({
    auth: {
      clientId: cfg.clientId,
      authority: `https://login.microsoftonline.com/${cfg.tenantId}`,
      redirectUri: `${window.location.origin}/`,
      postLogoutRedirectUri: `${window.location.origin}/`,
    },
    cache: { cacheLocation: 'sessionStorage' },
  });

  const scopes = [cfg.apiScope];
  const result = await pca.handleRedirectPromise();
  let account = result?.account ?? pca.getActiveAccount() ?? pca.getAllAccounts()[0] ?? null;

  if (!account) {
    try {
      sessionStorage.setItem(RETURN_HASH_KEY, window.location.hash);
    } catch {
      /* ignore */
    }
    await pca.loginRedirect({ scopes });
    return navigatingAway<AuthProvider>();
  }
  pca.setActiveAccount(account);

  // Restore the route the user asked for before the login round trip.
  try {
    const back = sessionStorage.getItem(RETURN_HASH_KEY);
    if (back !== null) {
      sessionStorage.removeItem(RETURN_HASH_KEY);
      if (back.startsWith('#/')) window.location.hash = back;
    }
  } catch {
    /* ignore */
  }

  // The token the sign-in redirect just returned: used until shortly before it expires, so the first API calls after
  // signing in never need a silent renewal.
  let fresh: { token: string; until: number } | null =
    result?.accessToken && result.expiresOn ? { token: result.accessToken, until: result.expiresOn.getTime() - 5 * 60_000 } : null;
  if (result) {
    try {
      sessionStorage.removeItem(RENEW_REDIRECT_KEY);
    } catch {
      /* ignore */
    }
  }

  /** A silent renewal that can't finish in the hidden iframe falls back to a full redirect, at most once a minute. */
  function mayRedirectToRenew(): boolean {
    try {
      const last = Number(sessionStorage.getItem(RENEW_REDIRECT_KEY) ?? 0);
      if (Date.now() - last < 60_000) return false;
      sessionStorage.setItem(RENEW_REDIRECT_KEY, String(Date.now()));
      sessionStorage.setItem(RETURN_HASH_KEY, window.location.hash);
    } catch {
      /* ignore */
    }
    return true;
  }

  async function token(): Promise<string> {
    if (fresh && Date.now() < fresh.until) return fresh.token;
    fresh = null;
    const acct = pca.getActiveAccount() ?? account;
    if (!acct) {
      await pca.loginRedirect({ scopes });
      return navigatingAway<string>();
    }
    try {
      const r = await pca.acquireTokenSilent({ scopes, account: acct });
      account = r.account ?? acct;
      return r.accessToken;
    } catch (e) {
      const code = (e as { errorCode?: string }).errorCode ?? '';
      if (e instanceof msal.InteractionRequiredAuthError || (SILENT_RENEWAL_FAILED.has(code) && mayRedirectToRenew())) {
        await pca.acquireTokenRedirect({ scopes, account: acct });
        return navigatingAway<string>();
      }
      throw e;
    }
  }

  return {
    mode: 'entra',
    async headers() {
      return { authorization: `Bearer ${await token()}` };
    },
    async streamParams() {
      return { access_token: await token() };
    },
    async signOut() {
      await pca.logoutRedirect({ account: pca.getActiveAccount() ?? undefined });
    },
  };
}

export async function createAuth(cfg: AppConfig): Promise<AuthProvider> {
  if (cfg.authMode === 'entra' && cfg.entra) return entraProvider(cfg.entra);
  return devProvider();
}
